import { useState, useEffect } from "react";
import type { TemplateStep, CreateTemplateStepForm, Team } from "@/types/task";
import type { UserProfile } from "@/types/user";
import {
  TbTrash,
  TbChevronDown,
  TbGripVertical,
  TbSearch,
} from "react-icons/tb";
import * as Label from "@radix-ui/react-label";
import * as Select from "@radix-ui/react-select";
import { useTemplateStore } from "@/stores/templateStore";

interface TemplateStepManagerProps {
  templateId: string;
  steps: TemplateStep[];
  onStepsChange: (steps: TemplateStep[]) => void;
  users?: UserProfile[];
  teams?: Team[];
}

interface LocalTemplateStepForm extends CreateTemplateStepForm {
  tempId: string;
  isExpanded?: boolean;
}

export const TemplateStepManager: React.FC<TemplateStepManagerProps> = ({
  templateId,
  steps,
  onStepsChange,
  users = [],
  teams = [],
}) => {
  // Get template data and actions from store
  const { validationError } = useTemplateStore();

  const [localSteps, setLocalSteps] = useState<LocalTemplateStepForm[]>(() =>
    steps.map((step) => ({
      tempId: step.id,
      step_name: step.step_name,
      description: step.description || "",
      step_order: step.step_order,
      sequence: step.sequence,
      default_assignee_type: step.default_assignee_type,
      default_assignee: step.default_assignee,
      is_required: step.is_required,
      auto_complete: step.auto_complete,
      requires_photos: step.requires_photos,
      requires_notes: step.requires_notes,
      reference_link: step.reference_link,
      survey_link: step.survey_link,
      depends_on_step_ids: step.depends_on_step_ids,
      completion_criteria: step.completion_criteria,
      reminder_days_before: step.reminder_days_before,
      notification_on_completion: step.notification_on_completion,
      escalation_days: step.escalation_days,
      isExpanded: false,
    }))
  );

  // Drag and drop state
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Search state for assignee dropdowns
  const [assigneeSearchTerms, setAssigneeSearchTerms] = useState<
    Record<string, string>
  >({});

  // Sync local steps when the steps prop changes (e.g., when editing a different template)
  useEffect(() => {
    const newLocalSteps = steps.map((step) => {
      // Find existing step to preserve expansion state
      const existingStep = localSteps.find((s) => s.tempId === step.id);

      return {
        tempId: step.id,
        step_name: step.step_name,
        description: step.description || "",
        step_order: step.step_order,
        sequence: step.sequence,
        default_assignee_type: step.default_assignee_type,
        default_assignee: step.default_assignee,
        is_required: step.is_required,
        auto_complete: step.auto_complete,
        requires_photos: step.requires_photos,
        requires_notes: step.requires_notes,
        reference_link: step.reference_link,
        survey_link: step.survey_link,
        depends_on_step_ids: step.depends_on_step_ids,
        completion_criteria: step.completion_criteria,
        reminder_days_before: step.reminder_days_before,
        notification_on_completion: step.notification_on_completion,
        escalation_days: step.escalation_days,
        isExpanded: existingStep?.isExpanded || false, // Preserve existing expansion state
      };
    });

    // Only update if the steps actually changed (to avoid infinite loops)
    if (
      JSON.stringify(steps.map((s) => s.id)) !==
      JSON.stringify(localSteps.map((s) => s.tempId))
    ) {
      setLocalSteps(newLocalSteps);
    }
  }, [steps, localSteps]);

  // Update parent when local steps change
  const updateParentSteps = (updatedSteps: LocalTemplateStepForm[]) => {
    const templateSteps: TemplateStep[] = updatedSteps.map((step, index) => ({
      id: step.tempId,
      template_task_id: templateId,
      step_name: step.step_name,
      description: step.description,
      step_order: index + 1, // Use 1-based indexing for step_order
      sequence: index + 1,
      default_assignee_type: step.default_assignee_type,
      default_assignee: step.default_assignee,
      is_required: step.is_required || false,
      auto_complete: step.auto_complete,
      requires_photos: step.requires_photos,
      requires_notes: step.requires_notes,
      reference_link: step.reference_link,
      survey_link: step.survey_link,
      depends_on_step_ids: step.depends_on_step_ids,
      completion_criteria: step.completion_criteria,
      reminder_days_before: step.reminder_days_before,
      notification_on_completion: step.notification_on_completion,
      escalation_days: step.escalation_days,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }));
    onStepsChange(templateSteps);
  };

  const addStep = () => {
    const newStep: LocalTemplateStepForm = {
      tempId: `temp_${Date.now()}`,
      step_name: "",
      description: "",
      step_order: localSteps.length,
      sequence: localSteps.length + 1,
      is_required: false,
      isExpanded: true,
    };
    const updatedSteps = [...localSteps, newStep];
    setLocalSteps(updatedSteps);
    updateParentSteps(updatedSteps);
  };

  const removeStep = (tempId: string) => {
    const updatedSteps = localSteps.filter((step) => step.tempId !== tempId);
    setLocalSteps(updatedSteps);
    updateParentSteps(updatedSteps);
  };

  const updateStep = (
    tempId: string,
    field: keyof LocalTemplateStepForm,
    value: string | number | boolean | undefined
  ) => {
    const updatedSteps = localSteps.map((step) => {
      if (step.tempId === tempId) {
        const updatedStep = { ...step, [field]: value };

        // Clear assignee when assignee type changes
        if (field === "default_assignee_type") {
          updatedStep.default_assignee = undefined;
          // Clear search term when assignee type changes
          setSearchTerm(tempId, "");
        }

        return updatedStep;
      }
      return step;
    });
    setLocalSteps(updatedSteps);
    updateParentSteps(updatedSteps);
  };

  const toggleStepExpansion = (tempId: string) => {
    setLocalSteps(
      localSteps.map((step) =>
        step.tempId === tempId
          ? { ...step, isExpanded: !step.isExpanded }
          : step
      )
    );
  };

  // Helper functions for search functionality
  const getSearchTerm = (stepId: string): string => {
    return assigneeSearchTerms[stepId] || "";
  };

  const setSearchTerm = (stepId: string, term: string) => {
    setAssigneeSearchTerms((prev) => ({
      ...prev,
      [stepId]: term,
    }));
  };

  const filterUsers = (searchTerm: string): UserProfile[] => {
    if (!searchTerm.trim()) return users;

    const term = searchTerm.toLowerCase();
    return users.filter((user) => {
      const fullName = `${user.first_name || ""} ${user.last_name || ""}`
        .trim()
        .toLowerCase();
      const email = user.email.toLowerCase();
      return fullName.includes(term) || email.includes(term);
    });
  };

  const filterTeams = (searchTerm: string): Team[] => {
    if (!searchTerm.trim()) return teams;

    const term = searchTerm.toLowerCase();
    return teams.filter((team) => team.name.toLowerCase().includes(term));
  };

  // Drag and Drop handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", index.toString());
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const updatedSteps = [...localSteps];
    const draggedStep = updatedSteps[draggedIndex];

    // Remove the dragged item
    updatedSteps.splice(draggedIndex, 1);

    // Insert it at the new position
    updatedSteps.splice(dropIndex, 0, draggedStep);

    // Update step_order and sequence based on new positions
    const reorderedSteps = updatedSteps.map((step, index) => ({
      ...step,
      step_order: index + 1, // 1-based indexing
      sequence: index + 1,
    }));

    setLocalSteps(reorderedSteps);

    // Update parent after a small delay to ensure state is set
    setTimeout(() => {
      updateParentSteps(reorderedSteps);
    }, 0);

    // Reset drag state
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
    // Force a re-render by updating the state
    if (draggedIndex !== null) {
      setLocalSteps([...localSteps]);
    }
  };

  return (
    <div className="flex flex-col items-start w-full overflow-y-auto min-h-[60vh] max-h-[60vh] p-4 rounded-[10px] border border-gray-100">
      {/* Show validation error if any */}
      {validationError && (
        <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded mb-4">
          {validationError}
        </div>
      )}
      <div className="space-y-4 w-full">
        {localSteps.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-[#828282] text-[12px] mb-4">
              No steps added yet.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {localSteps.map((step, index) => (
              <div
                key={step.tempId} // Use only tempId for stable key
                className={`bg-gray-100 border border-gray-100 rounded-2xl overflow-hidden transition-transform ${
                  draggedIndex === index ? "opacity-50 scale-95" : ""
                } ${
                  dragOverIndex === index ? "border-[#374795] border-2" : ""
                }`}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
              >
                {/* Step Header - Draggable */}
                <div
                  className="p-4 cursor-grab active:cursor-grabbing"
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragEnd={handleDragEnd}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <TbGripVertical
                        size={24}
                        className="text-[#828282] cursor-grab"
                      />
                      <span className="text-[#444444] text-[12px] font-normal">
                        Step {index + 1} -
                      </span>
                      <span className="text-[#444444] text-[12px] font-simibold">
                        {step.step_name}
                      </span>
                    </div>
                    <div className="flex items-center gap-5">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeStep(step.tempId);
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        className="text-red-400 cursor-pointer"
                      >
                        <TbTrash size={24} />
                      </button>
                      <div className="w-0 h-[17px] border-l border-gray-300"></div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleStepExpansion(step.tempId);
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        className="text-[#828282] cursor-pointer"
                      >
                        <TbChevronDown
                          size={20}
                          className={`transition-transform ${
                            step.isExpanded ? "rotate-180" : "rotate-0"
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Step Form - Expanded */}
                {step.isExpanded && (
                  <div className="bg-white">
                    <div className="p-4 space-y-3">
                      {/* Step Name */}
                      <div className="space-y-1.5">
                        <Label.Root className="text-[#444444] text-[14px] font-semibold">
                          Step Name
                        </Label.Root>
                        <div className="bg-gray-100 rounded-[10px] px-3.5 py-[13px]">
                          <input
                            type="text"
                            value={step.step_name}
                            onChange={(e) =>
                              updateStep(
                                step.tempId,
                                "step_name",
                                e.target.value
                              )
                            }
                            className="w-full bg-transparent text-[#828282] text-[12px] placeholder:text-[#828282] border-none outline-none"
                            placeholder="Type Here"
                            maxLength={50}
                          />
                        </div>
                        <div className="flex justify-end mt-1">
                          <span
                            className={`text-[12px] font-medium ${step.step_name.length >= 50 ? "text-red-500" : "text-[#828282]"}`}
                          >
                            {step.step_name.length}/50
                          </span>
                        </div>
                      </div>

                      {/* Step Description */}
                      <div className="space-y-1.5">
                        <Label.Root className="text-[#444444] text-[14px] font-semibold">
                          Step Descriptions
                        </Label.Root>
                        <div className="bg-gray-100 rounded-[10px] px-3.5 py-[13px]">
                          <textarea
                            value={step.description}
                            onChange={(e) =>
                              updateStep(
                                step.tempId,
                                "description",
                                e.target.value
                              )
                            }
                            className="w-full bg-transparent text-[#828282] text-[12px] placeholder:text-[#828282] border-none outline-none resize-none"
                            placeholder="Type Here"
                            rows={2}
                            maxLength={1000}
                          />
                        </div>
                        <div className="flex justify-end mt-1">
                          <span
                            className={`text-[12px] font-medium ${(step.description?.length || 0) >= 1000 ? "text-red-500" : "text-[#828282]"}`}
                          >
                            {step.description?.length || 0}/1000
                          </span>
                        </div>
                      </div>

                      {/* Assignment Type & Assignee */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label.Root className="text-[#444444] text-[14px] font-semibold">
                            Assignment Type
                          </Label.Root>
                          <Select.Root
                            value={step.default_assignee_type || "none"}
                            onValueChange={(value) =>
                              updateStep(
                                step.tempId,
                                "default_assignee_type",
                                value === "none" ? undefined : value
                              )
                            }
                          >
                            <Select.Trigger className="w-full bg-gray-100 rounded-[10px] px-3.5 py-[13px] flex items-center justify-between text-[#828282] text-[12px]">
                              <Select.Value placeholder="Select" />
                              <Select.Icon>
                                <TbChevronDown
                                  size={16}
                                  className="text-[#828282] rotate-270"
                                />
                              </Select.Icon>
                            </Select.Trigger>
                            <Select.Portal>
                              <Select.Content className="z-300000 bg-white rounded-xl shadow-[0px_0px_24px_0px_rgba(0,0,0,0.08)] border-0 max-h-60 overflow-auto p-1">
                                <Select.Viewport>
                                  <Select.Item
                                    value="none"
                                    className="relative cursor-pointer select-none py-2.5 px-3 mx-1 rounded-lg hover:bg-gray-50 focus:bg-gray-50 data-[state=checked]:bg-blue-50 data-[state=checked]:text-blue-700 text-[#444444] text-[12px] font-medium transition-colors outline-none"
                                  >
                                    <Select.ItemText>
                                      No Default
                                    </Select.ItemText>
                                  </Select.Item>
                                  <Select.Item
                                    value="User"
                                    className="relative cursor-pointer select-none py-2.5 px-3 mx-1 rounded-lg hover:bg-gray-50 focus:bg-gray-50 data-[state=checked]:bg-blue-50 data-[state=checked]:text-blue-700 text-[#444444] text-[12px] font-medium transition-colors outline-none"
                                  >
                                    <Select.ItemText>User</Select.ItemText>
                                  </Select.Item>
                                  <Select.Item
                                    value="Team"
                                    className="relative cursor-pointer select-none py-2.5 px-3 mx-1 rounded-lg hover:bg-gray-50 focus:bg-gray-50 data-[state=checked]:bg-blue-50 data-[state=checked]:text-blue-700 text-[#444444] text-[12px] font-medium transition-colors outline-none"
                                  >
                                    <Select.ItemText>Team</Select.ItemText>
                                  </Select.Item>
                                </Select.Viewport>
                              </Select.Content>
                            </Select.Portal>
                          </Select.Root>
                        </div>

                        <div className="space-y-1.5">
                          <Label.Root className="text-[#444444] text-[14px] font-semibold">
                            Assignee
                          </Label.Root>
                          <Select.Root
                            value={step.default_assignee || "none"}
                            onValueChange={(value) =>
                              updateStep(
                                step.tempId,
                                "default_assignee",
                                value === "none" ? undefined : value
                              )
                            }
                            disabled={!step.default_assignee_type}
                          >
                            <Select.Trigger className="w-full bg-gray-100 rounded-[10px] px-3.5 py-[13px] flex items-center justify-between text-[#828282] text-[12px] disabled:opacity-50">
                              <Select.Value
                                placeholder={
                                  step.default_assignee_type
                                    ? "Select"
                                    : "Select assignment type first"
                                }
                              />
                              <Select.Icon>
                                <TbChevronDown
                                  size={16}
                                  className="text-[#828282] rotate-270"
                                />
                              </Select.Icon>
                            </Select.Trigger>
                            <Select.Portal>
                              <Select.Content className="z-300000 bg-white rounded-xl shadow-[0px_0px_24px_0px_rgba(0,0,0,0.08)] border-0 max-h-60 overflow-auto p-1">
                                <Select.Viewport>
                                  {/* Search Input */}
                                  {step.default_assignee_type && (
                                    <div className="sticky top-0 bg-white p-2 border-b border-gray-100">
                                      <div className="relative">
                                        <TbSearch
                                          size={14}
                                          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                        />
                                        <input
                                          type="text"
                                          placeholder={`Search ${step.default_assignee_type.toLowerCase()}s...`}
                                          value={getSearchTerm(step.tempId)}
                                          onChange={(e) =>
                                            setSearchTerm(
                                              step.tempId,
                                              e.target.value
                                            )
                                          }
                                          className="w-full pl-8 pr-3 py-2 text-[12px] bg-gray-50 rounded-lg border-0 outline-none focus:bg-white focus:ring-1 focus:ring-blue-200"
                                          onClick={(e) => e.stopPropagation()}
                                          onKeyDown={(e) => e.stopPropagation()}
                                        />
                                      </div>
                                    </div>
                                  )}

                                  <Select.Item
                                    value="none"
                                    className="relative cursor-pointer select-none py-2.5 px-3 mx-1 rounded-lg hover:bg-gray-50 focus:bg-gray-50 data-[state=checked]:bg-blue-50 data-[state=checked]:text-blue-700 text-[#444444] text-[12px] font-medium transition-colors outline-none"
                                  >
                                    <Select.ItemText>
                                      No Default
                                    </Select.ItemText>
                                  </Select.Item>

                                  {step.default_assignee_type === "User" &&
                                    filterUsers(getSearchTerm(step.tempId)).map(
                                      (user) => (
                                        <Select.Item
                                          key={user.id}
                                          value={user.id}
                                          className="relative cursor-pointer select-none py-2.5 px-3 mx-1 rounded-lg hover:bg-gray-50 focus:bg-gray-50 data-[state=checked]:bg-blue-50 data-[state=checked]:text-blue-700 text-[#444444] text-[12px] font-medium transition-colors outline-none"
                                        >
                                          <Select.ItemText>
                                            {user.first_name || user.last_name
                                              ? `${user.first_name || ""} ${
                                                  user.last_name || ""
                                                }`.trim()
                                              : user.email}
                                          </Select.ItemText>
                                        </Select.Item>
                                      )
                                    )}

                                  {step.default_assignee_type === "Team" &&
                                    filterTeams(getSearchTerm(step.tempId)).map(
                                      (team) => (
                                        <Select.Item
                                          key={team.id}
                                          value={team.id}
                                          className="relative cursor-pointer select-none py-2.5 px-3 mx-1 rounded-lg hover:bg-gray-50 focus:bg-gray-50 data-[state=checked]:bg-blue-50 data-[state=checked]:text-blue-700 text-[#444444] text-[12px] font-medium transition-colors outline-none"
                                        >
                                          <Select.ItemText>
                                            {team.name}
                                          </Select.ItemText>
                                        </Select.Item>
                                      )
                                    )}

                                  {/* No Results Message */}
                                  {step.default_assignee_type === "User" &&
                                    getSearchTerm(step.tempId) &&
                                    filterUsers(getSearchTerm(step.tempId))
                                      .length === 0 && (
                                      <div className="py-2.5 px-3 mx-1 text-[#828282] text-[12px] text-center">
                                        No users found
                                      </div>
                                    )}

                                  {step.default_assignee_type === "Team" &&
                                    getSearchTerm(step.tempId) &&
                                    filterTeams(getSearchTerm(step.tempId))
                                      .length === 0 && (
                                      <div className="py-2.5 px-3 mx-1 text-[#828282] text-[12px] text-center">
                                        No teams found
                                      </div>
                                    )}
                                </Select.Viewport>
                              </Select.Content>
                            </Select.Portal>
                          </Select.Root>
                        </div>
                      </div>

                      {/* Reference Link & Survey Link */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label.Root className="text-[#444444] text-[14px] font-semibold">
                            Reference Link
                          </Label.Root>
                          <div className="bg-gray-100 rounded-[10px] px-3.5 py-[13px]">
                            <input
                              type="url"
                              value={step.reference_link || ""}
                              onChange={(e) =>
                                updateStep(
                                  step.tempId,
                                  "reference_link",
                                  e.target.value
                                )
                              }
                              className="w-full bg-transparent text-[#828282] text-[12px] placeholder:text-[#828282] border-none outline-none"
                              placeholder="Type Here"
                              maxLength={250}
                            />
                          </div>
                        </div>

                        <div className="space-y-1.5">
                          <Label.Root className="text-[#444444] text-[14px] font-semibold">
                            Survey Link
                          </Label.Root>
                          <div className="bg-gray-100 rounded-[10px] px-3.5 py-[13px]">
                            <input
                              type="text"
                              value={step.survey_link || ""}
                              onChange={(e) =>
                                updateStep(
                                  step.tempId,
                                  "survey_link",
                                  e.target.value
                                )
                              }
                              className="w-full bg-transparent text-[#828282] text-[12px] placeholder:text-[#828282] border-none outline-none"
                              placeholder="Type Here"
                              maxLength={250}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Save Button */}
                      <button className="px-6 py-3 border border-[#374795] rounded-[10px] text-[#374795] text-[14px] font-semibold  hover:bg-gray-100 hover:custom-b-marian-blue hover:text-[#374795] cursor-pointer">
                        Save
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-center gap-4">
          <button
            onClick={addStep}
            className="px-6 py-3 border border-[#374795] rounded-[10px] text-[#374795] text-[14px] font-semibold hover:bg-gray-100 hover:custom-b-marian-blue hover:text-[#374795] cursor-pointer"
          >
            New Step
          </button>
        </div>
      </div>
    </div>
  );
};
